import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Bot } from 'grts-utils/types/Bot';
import { Exchange } from 'grts-utils/types/Exchange';
import { Subscription } from '../../../types/subscription';
import { selectUserId } from '../../../store/slices/user';
import {
  useGetAllAvailableBalancesFromExchangesQuery,
  useGetAvailableBalanceQuery,
  useGetTopBotsQuery,
  useRemoveSubscriptionMutation,
  useStartComplexParamsCalculationMutation,
  useUpdateFeatureFlagMutation,
  useUpdateMrcRsiFeatureFlagMutation,
  useUpdateSubscriptionBalanceMutation,
  useUpdateSubscriptionPairMutation,
  useUpdateSubscriptionStateMutation,
} from '../../../store/services/api';
import { formatBalance, formatNumber } from '../../../helpers/NumberFormatter';
import { tokens } from '../Content/Calculations/tokens';
import EditorialHeader from '../Content/Calculations/Result/shared/EditorialHeader';
import {
  Action,
  DeltaInput,
  DropdownCell,
  EditorialButton,
  EditorialCheck,
  FlagToggle,
  InfoBanner,
  StaticCell,
  cellGrid,
} from './atoms';

interface Props {
  subscription: Subscription;
  bot: Bot | undefined;
}

/**
 * Editorial Manage page (design: manage-variations.jsx > Manage_01_Chapters).
 * Six independent actions, each wired to the SAME RTK mutations the live Manage
 * tab uses — only the presentation is new.
 */

// ── 01 Change pair ─────────────────────────────────────────────
const ChangePairAction = ({ subscription }: { subscription: Subscription }) => {
  const userId = useSelector(selectUserId);
  const [updateSubscriptionPair] = useUpdateSubscriptionPairMutation();
  const { data: botsData } = useGetTopBotsQuery(
    { userId, query: subscription.baseAsset },
    { refetchOnMountOrArgChange: true },
  );
  const { data: balancesData, refetch } =
    useGetAllAvailableBalancesFromExchangesQuery(userId);

  const [selectedExchange, setSelectedExchange] = useState<Exchange>(
    subscription.exchange,
  );
  const [selectedQuoteAsset, setSelectedQuoteAsset] = useState(
    subscription.quoteAsset,
  );
  const [shouldMakeTransfer, setShouldMakeTransfer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
  const [error, setError] = useState<string | undefined>();

  const exchanges = useMemo(
    () =>
      (botsData?.assets?.[subscription.baseAsset] ?? [])
        .map((p) => p.exchange)
        .filter((p, i, s) => s.indexOf(p) === i),
    [botsData?.assets, subscription.baseAsset],
  );
  const quoteAssets = useMemo(
    () =>
      (botsData?.assets?.[subscription.baseAsset] ?? [])
        .filter((p) => p.exchange === selectedExchange)
        .map((p) => p.quoteAsset)
        .filter((p, i, s) => s.indexOf(p) === i),
    [botsData?.assets, selectedExchange, subscription.baseAsset],
  );

  const handleExchangeChange = (exchange: string) => {
    setSelectedExchange(exchange as Exchange);
    const nextQuotes = (botsData?.assets?.[subscription.baseAsset] ?? [])
      .filter((p) => p.exchange === exchange)
      .map((p) => p.quoteAsset);
    if (nextQuotes.length && !nextQuotes.includes(selectedQuoteAsset)) {
      setSelectedQuoteAsset(nextQuotes[0]);
    }
  };

  const availableStable =
    balancesData?.balances?.[selectedExchange]?.[selectedQuoteAsset] ?? 0;
  const availableAsset =
    balancesData?.balances?.[selectedExchange]?.[subscription.baseAsset] ?? 0;

  const hasEnoughStable = availableStable >= subscription.stableBalance;
  const hasEnoughAsset =
    selectedExchange === subscription.exchange ||
    availableAsset >= subscription.assetBalance;
  const isUnchanged =
    selectedExchange === subscription.exchange &&
    selectedQuoteAsset === subscription.quoteAsset;

  // Same exchange, different quote, not enough of the new quote: offer to sell
  // this bot's current quote balance into the new one to fund the switch.
  const showBalanceTransferOption =
    selectedExchange === subscription.exchange &&
    selectedQuoteAsset !== subscription.quoteAsset &&
    !hasEnoughStable;

  // Don't block on balance — warn instead and let the backend validate (matches
  // the live Manage tab). Only structurally-invalid states disable Confirm.
  const disabled =
    isLoading || isUnchanged || !exchanges.length || !quoteAssets.length;

  const handleSubmit = async () => {
    setIsLoading(true);
    const result = await updateSubscriptionPair({
      userId,
      subscriptionId: subscription.id,
      quoteAsset: selectedQuoteAsset,
      exchange: selectedExchange,
      shouldMakeTransfer,
    });
    setIsSuccess(!!result.data?.success);
    setError(result.data?.error);
    setIsLoading(false);
    if (result.data?.success) refetch();
  };

  return (
    <Action
      n="01"
      title="Change pair"
      intro="Switch the engine to a different quote asset or exchange. Requires available balance on the target pair."
      accent={tokens.green}
      body={
        <>
          <div style={cellGrid}>
            <DropdownCell
              label="Exchange"
              value={selectedExchange}
              options={exchanges}
              onChange={handleExchangeChange}
              accent={tokens.green}
              disabled={exchanges.length < 2}
            />
            <DropdownCell
              label="Quote Asset"
              value={selectedQuoteAsset}
              options={quoteAssets}
              onChange={setSelectedQuoteAsset}
              accent={tokens.green}
              disabled={quoteAssets.length < 2}
            />
          </div>
          <InfoBanner accent={isSuccess ? tokens.green : tokens.amber}>
            Availability: {selectedQuoteAsset}{' '}
            {formatNumber(availableStable, 2)} · {subscription.baseAsset}{' '}
            {formatNumber(availableAsset, 6)}
            {!hasEnoughStable &&
              ` — not enough ${selectedQuoteAsset} on ${selectedExchange}.`}
            {!hasEnoughAsset &&
              ` — not enough ${subscription.baseAsset} on ${selectedExchange}.`}
            {selectedExchange !== subscription.exchange &&
              ' Fund the target exchange before switching.'}
            {isSuccess && ' — new pair selected.'}
            {error && ` — ${error}`}
          </InfoBanner>
          {showBalanceTransferOption && (
            <div style={{ marginTop: 16 }}>
              <EditorialCheck
                checked={shouldMakeTransfer}
                onChange={setShouldMakeTransfer}
                color={tokens.amber}
              >
                <div
                  style={{ fontSize: 13, color: tokens.amber, lineHeight: 1.6 }}
                >
                  Sell this bot&apos;s {subscription.quoteAsset} balance into{' '}
                  {selectedQuoteAsset} so there is enough to make the switch.
                </div>
              </EditorialCheck>
            </div>
          )}
          <div style={{ marginTop: 18 }}>
            <EditorialButton
              accent={tokens.green}
              disabled={disabled}
              onClick={handleSubmit}
            >
              {isLoading ? 'Working…' : 'Confirm'}
            </EditorialButton>
          </div>
        </>
      }
    />
  );
};

// ── 02 Adjust balance ──────────────────────────────────────────
const AdjustBalanceAction = ({
  subscription,
  maxDecimals,
}: {
  subscription: Subscription;
  maxDecimals: number;
}) => {
  const userId = useSelector(selectUserId);
  const [updateSubscriptionBalance] = useUpdateSubscriptionBalanceMutation();
  const { data: availableStable, refetch: refetchStable } =
    useGetAvailableBalanceQuery({
      userId,
      subscriptionId: subscription.id,
      asset: subscription.quoteAsset,
    });
  const { refetch: refetchAsset } = useGetAvailableBalanceQuery({
    userId,
    subscriptionId: subscription.id,
    asset: subscription.baseAsset,
  });

  const [stableDelta, setStableDelta] = useState(0);
  const [assetDelta, setAssetDelta] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  // Editing after a submit clears the previous result message.
  const handleStableDelta = (value: number) => {
    setStableDelta(value);
    setDone(false);
    setFailed(false);
  };
  const handleAssetDelta = (value: number) => {
    setAssetDelta(value);
    setDone(false);
    setFailed(false);
  };

  const newStable = subscription.stableBalance + stableDelta;
  const newAsset = subscription.assetBalance + assetDelta;

  const maxAssetAddable = availableStable?.balance
    ? availableStable.balance / subscription.latestPrice
    : 0;

  const stableTooHigh =
    stableDelta > 0 &&
    typeof availableStable?.balance === 'number' &&
    stableDelta > availableStable.balance;
  const assetTooHigh = assetDelta > 0 && assetDelta > maxAssetAddable;
  const wouldGoNegative = newStable < 0 || newAsset < 0;
  const nothingChanged = stableDelta === 0 && assetDelta === 0;

  const disabled =
    isLoading ||
    nothingChanged ||
    stableTooHigh ||
    assetTooHigh ||
    wouldGoNegative;

  let bannerAccent: string = tokens.amber;
  if (stableTooHigh || assetTooHigh || wouldGoNegative || failed) {
    bannerAccent = tokens.red;
  } else if (done) {
    bannerAccent = tokens.green;
  }

  const handleSubmit = async () => {
    if (disabled) return;
    setIsLoading(true);
    setDone(false);
    setFailed(false);
    const result = await updateSubscriptionBalance({
      userId,
      subscriptionId: subscription.id,
      stableBalance: stableDelta !== 0 ? newStable : undefined,
      assetBalance: assetDelta !== 0 ? newAsset : undefined,
      // An asset change must be executed on the exchange (buy/sell the delta).
      shouldOrder: assetDelta !== 0,
    });
    setIsLoading(false);
    if ('data' in result && result.data?.success) {
      setDone(true);
      setStableDelta(0);
      setAssetDelta(0);
    } else {
      setFailed(true);
    }
    refetchStable();
    refetchAsset();
  };

  return (
    <Action
      n="02"
      title="Adjust balance"
      intro="Add to or remove from the balance the engine manages. An asset change is executed on the exchange at the next tick."
      accent={tokens.green}
      body={
        <>
          <div style={{ ...cellGrid, gap: 28 }}>
            <DeltaInput
              label="Stable"
              current={formatNumber(subscription.stableBalance, 2)}
              currentNum={subscription.stableBalance}
              unit={subscription.quoteAsset}
              delta={stableDelta}
              onDelta={handleStableDelta}
              accent={tokens.green}
              decimals={2}
              step={1}
            />
            <DeltaInput
              label="Asset"
              current={formatNumber(subscription.assetBalance, maxDecimals)}
              currentNum={subscription.assetBalance}
              unit={subscription.baseAsset}
              delta={assetDelta}
              onDelta={handleAssetDelta}
              accent={tokens.green}
              decimals={maxDecimals}
              step={0.001}
            />
          </div>
          {(stableDelta !== 0 || assetDelta !== 0 || done || failed) && (
            <InfoBanner accent={bannerAccent}>
              Available: {subscription.quoteAsset}{' '}
              {formatNumber(availableStable?.balance ?? 0, 2)} · up to{' '}
              {formatNumber(maxAssetAddable, maxDecimals)}{' '}
              {subscription.baseAsset}.
              {stableTooHigh &&
                ` Not enough ${subscription.quoteAsset} available.`}
              {assetTooHigh && ` Exceeds buyable ${subscription.baseAsset}.`}
              {wouldGoNegative && ' Balance cannot go below zero.'}
              {assetDelta !== 0 && ' Applying places a market order.'}
              {done && ' Applied.'}
              {failed && ' Update failed — please try again.'}
            </InfoBanner>
          )}
          <div style={{ marginTop: 18 }}>
            <EditorialButton
              accent={tokens.green}
              dark
              disabled={disabled}
              onClick={handleSubmit}
            >
              {isLoading ? 'Working…' : 'Apply'}
            </EditorialButton>
          </div>
        </>
      }
    />
  );
};

// ── 03 Run complex params ──────────────────────────────────────
const RunComplexParamsAction = ({ bot }: { bot: Bot | undefined }) => {
  const [startComplexParamsCalculation] =
    useStartComplexParamsCalculationMutation();
  const [didStart, setDidStart] = useState<boolean | undefined>();
  const [didError, setDidError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const lastRun = bot?.complexParams?.lastUpdated
    ? new Date(bot.complexParams.lastUpdated).toLocaleString()
    : '—';
  const duration = bot?.complexParams?.durationCPC
    ? `≈${Math.round(bot.complexParams.durationCPC / 1000)}s`
    : '—';

  const handleSubmit = async () => {
    if (!bot) return;
    setIsLoading(true);
    setDidError(false);
    try {
      const result = await startComplexParamsCalculation({
        botFriendlyName: bot.botId,
        skipQueue: true,
      });
      if (result.error) setDidError(true);
      if (result.data?.didStart !== undefined)
        setDidStart(result.data.didStart);
    } catch {
      setDidError(true);
    }
    setIsLoading(false);
  };

  return (
    <Action
      n="03"
      title="Run complex params"
      intro="Recompute the cube-root price band and indicator thresholds against the latest market data. Safe to run — no balances are touched."
      accent={tokens.cyan}
      body={
        <>
          <div style={cellGrid}>
            <StaticCell
              label="Last run"
              value={lastRun}
              accent={tokens.cyan}
              sz={22}
            />
            <StaticCell
              label="Duration"
              value={duration}
              accent={tokens.cyan}
              sz={22}
            />
          </div>
          <div style={{ marginTop: 18 }}>
            <EditorialButton
              accent={tokens.cyan}
              dark
              disabled={!bot || isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? 'Working…' : 'Start calculation'}
            </EditorialButton>
          </div>
          {didStart !== undefined && (
            <InfoBanner accent={tokens.cyan}>
              {didStart
                ? 'Complex params calculation has begun. This will take a while.'
                : 'Request has been added to the queue.'}
            </InfoBanner>
          )}
          {didError && (
            <InfoBanner accent={tokens.red}>Something went wrong.</InfoBanner>
          )}
        </>
      }
    />
  );
};

// ── 04 Feature flags ───────────────────────────────────────────
const FeatureFlagsAction = ({ bot }: { bot: Bot | undefined }) => {
  const [updateFeatureFlag] = useUpdateFeatureFlagMutation();
  const [updateMrcRsiFeatureFlag] = useUpdateMrcRsiFeatureFlagMutation();
  const [takeProfit, setTakeProfit] = useState(bot?.featureFlag ?? false);
  const [mrcRsi, setMrcRsi] = useState(bot?.mrcRsiFeatureFlag ?? false);

  // `bot` arrives asynchronously, so sync the toggles once its real value loads
  // (and whenever it changes elsewhere).
  useEffect(() => {
    setTakeProfit(bot?.featureFlag ?? false);
  }, [bot?.featureFlag]);

  useEffect(() => {
    setMrcRsi(bot?.mrcRsiFeatureFlag ?? false);
  }, [bot?.mrcRsiFeatureFlag]);

  const handleToggle = async (next: boolean) => {
    if (!bot) return;
    setTakeProfit(next);
    await updateFeatureFlag({ botId: bot.id, featureFlag: next });
  };

  const handleMrcRsiToggle = async (next: boolean) => {
    if (!bot) return;
    setMrcRsi(next);
    await updateMrcRsiFeatureFlag({ botId: bot.id, mrcRsiFeatureFlag: next });
  };

  return (
    <Action
      n="04"
      title="Feature flags"
      intro="Toggle pair-specific behaviour. Flags take effect on the next decision tick."
      accent={tokens.cyan}
      body={
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '24px 1fr',
            gap: 16,
            padding: '16px 0',
            alignItems: 'center',
            borderTop: `1px solid ${tokens.cyan}55`,
          }}
        >
          <FlagToggle
            on={takeProfit}
            color={tokens.green}
            onChange={handleToggle}
          />
          <div>
            <div
              style={{
                fontSize: 15,
                color: tokens.textPrimary,
                fontWeight: 600,
              }}
            >
              Take Profit Transaction
            </div>
            <div
              style={{
                fontSize: 12,
                color: tokens.textMuted,
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Allow take-profit triggers to fire mid-position.
            </div>
          </div>
          <FlagToggle
            on={mrcRsi}
            color={tokens.green}
            onChange={handleMrcRsiToggle}
          />
          <div>
            <div
              style={{
                fontSize: 15,
                color: tokens.textPrimary,
                fontWeight: 600,
              }}
            >
              RSI-MRC
            </div>
            <div
              style={{
                fontSize: 12,
                color: tokens.textMuted,
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Delay orders after a cbrt-range resolves until RSI cycles through
              an overextended zone.
            </div>
          </div>
        </div>
      }
    />
  );
};

// ── 05 Pause subscription ──────────────────────────────────────
const PauseAction = ({
  subscription,
  maxDecimals,
}: {
  subscription: Subscription;
  maxDecimals: number;
}) => {
  const userId = useSelector(selectUserId);
  const [updateSubscriptionState] = useUpdateSubscriptionStateMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async () => {
    setIsLoading(true);
    await updateSubscriptionState({
      userId,
      subscriptionId: subscription.id,
      isActive: !subscription.isActive,
    });
    setIsLoading(false);
  };

  let pauseLabel = 'Resume subscription';
  if (isLoading) pauseLabel = 'Working…';
  else if (subscription.isActive) pauseLabel = 'Pause subscription';

  return (
    <Action
      n="05"
      title="Pause subscription"
      intro="When paused, the program does not touch the assigned balances. If you pause while it had bought, you are exposed to a downward market."
      accent={tokens.amber}
      body={
        <div
          style={{
            padding: '24px 0',
            borderTop: `2px solid ${tokens.amber}55`,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 32,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                color: tokens.amber,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.18em',
              }}
            >
              CURRENT POSITION
            </div>
            <div
              style={{
                fontSize: 36,
                fontFamily: tokens.fontDisplay,
                color: tokens.textPrimary,
                lineHeight: 1,
                marginTop: 6,
                letterSpacing: '-0.01em',
              }}
            >
              {formatNumber(subscription.assetBalance, maxDecimals)}{' '}
              <span style={{ color: tokens.textFaint }}>
                {subscription.baseAsset}
              </span>
              <span style={{ color: tokens.textFaint, padding: '0 14px' }}>
                +
              </span>
              {formatBalance(subscription.stableBalance, false)}{' '}
              <span style={{ color: tokens.textFaint }}>
                {subscription.quoteAsset}
              </span>
            </div>
          </div>
          <EditorialButton accent={tokens.amber} onClick={handleToggle}>
            {pauseLabel}
          </EditorialButton>
        </div>
      }
    />
  );
};

// ── 06 Remove subscription ─────────────────────────────────────
const RemoveAction = ({
  subscription,
  maxDecimals,
}: {
  subscription: Subscription;
  maxDecimals: number;
}) => {
  const userId = useSelector(selectUserId);
  const [removeSubscription] = useRemoveSubscriptionMutation();
  const [shouldSell, setShouldSell] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const canSell = !!subscription.assetBalance;

  const handleSubmit = async () => {
    if (!userId) return;
    setIsLoading(true);
    await removeSubscription({
      userId,
      subscriptionId: subscription.id,
      shouldSell,
    });
    setIsLoading(false);
  };

  return (
    <Action
      n="06"
      title="Remove subscription"
      intro="The bot is decommissioned, held assets are unwound at market, and the subscription record is archived. Irreversible."
      accent={tokens.red}
      body={
        <>
          {canSell && (
            <EditorialCheck
              checked={shouldSell}
              onChange={setShouldSell}
              color={tokens.amber}
            >
              <div
                style={{ fontSize: 14, color: tokens.amber, lineHeight: 1.6 }}
              >
                By selecting this checkbox, you are telling the program to sell
                all remaining assets the bot holds. In this case, that will be{' '}
                <b style={{ color: tokens.textPrimary }}>
                  {formatNumber(subscription.assetBalance, maxDecimals)}{' '}
                  {subscription.baseAsset}
                </b>
                .
              </div>
            </EditorialCheck>
          )}
          <div style={{ marginTop: 18 }}>
            <EditorialButton
              accent={tokens.red}
              dark
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? 'Working…' : 'Remove subscription'}
            </EditorialButton>
          </div>
        </>
      }
    />
  );
};

const Manage = ({ subscription, bot }: Props) => {
  const maxDecimals = bot?.complexParams?.maxDecimals ?? 6;

  return (
    <div
      style={{
        // Transparent so the content root's profit wash shows through the top.
        background: 'transparent',
        padding: '40px 0',
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <EditorialHeader number="04" label="Manage" />
      <ChangePairAction subscription={subscription} />
      <AdjustBalanceAction
        subscription={subscription}
        maxDecimals={maxDecimals}
      />
      <RunComplexParamsAction bot={bot} />
      <FeatureFlagsAction bot={bot} />
      <PauseAction subscription={subscription} maxDecimals={maxDecimals} />
      <RemoveAction subscription={subscription} maxDecimals={maxDecimals} />
    </div>
  );
};

export default Manage;
